import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { LineChart } from '@mui/x-charts/LineChart';

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

AreaGradient.propTypes = {
  color: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

function getDaysInMonth(month, year) {
  const date = new Date(year, month, 0);
  const monthName = date.toLocaleDateString('en-US', {
    month: 'short',
  });
  const daysInMonth = date.getDate();
  const days = [];
  let i = 1;
  while (days.length < daysInMonth) {
    days.push(`${monthName} ${i}`);
    i += 1;
  }
  return days;
}

function buildXAxisLabels(categories, monthZeroBased, year) {
  if (!categories?.length || monthZeroBased == null || !year) return [];
  const date = new Date(year, monthZeroBased, 1);
  const monthName = date.toLocaleDateString('en-US', { month: 'short' });
  return categories.map((d) => `${monthName} ${Number(d) || d}`);
}

const DEFAULT_SERIES = [
  {
    id: 'direct',
    label: 'Direct',
    showMark: false,
    curve: 'linear',
    stack: 'total',
    area: true,
    stackOrder: 'ascending',
    data: [
      300, 900, 600, 1200, 1500, 1800, 2400, 2100, 2700, 3000, 1800, 3300,
      3600, 3900, 4200, 4500, 3900, 4800, 5100, 5400, 4800, 5700, 6000,
      6300, 6600, 6900, 7200, 7500, 7800, 8100,
    ],
  },
  {
    id: 'referral',
    label: 'Referral',
    showMark: false,
    curve: 'linear',
    stack: 'total',
    area: true,
    stackOrder: 'ascending',
    data: [
      500, 900, 700, 1400, 1100, 1700, 2300, 2000, 2600, 2900, 2300, 3200,
      3500, 3800, 4100, 4400, 2900, 4700, 5000, 5300, 5600, 5900, 6200,
      6500, 5600, 6800, 7100, 7400, 7700, 8000,
    ],
  },
  {
    id: 'organic',
    label: 'Organic',
    showMark: false,
    curve: 'linear',
    stack: 'total',
    stackOrder: 'ascending',
    data: [
      1000, 1500, 1200, 1700, 1300, 2000, 2400, 2200, 2600, 2800, 2500,
      3000, 3400, 3700, 3200, 3900, 4100, 3500, 4300, 4500, 4000, 4700,
      5000, 5200, 4800, 5400, 5600, 5900, 6100, 6300,
    ],
    area: true,
  },
];

export default function SessionsChart({
  title = 'Sessions',
  caption = 'Sessions per day for the last 30 days',
  summaryValue = '13,277',
  trendLabel = '+35%',
  trendColor = 'success',
  xAxisData,
  series: seriesProp,
  selectedMonth,
  selectedYear,
  hideSummary,
}) {
  const theme = useTheme();
  const isCustom =
    Boolean(seriesProp?.length) &&
    Boolean(xAxisData?.length || (selectedMonth && selectedYear));
  const data = xAxisData?.length
    ? xAxisData
    : buildXAxisLabels(
        isCustom && seriesProp?.[0]?.data?.length
          ? Array.from({ length: seriesProp[0].data.length }, (_, i) => String(i + 1))
          : [],
        selectedMonth,
        selectedYear,
      ).length
      ? buildXAxisLabels(
          Array.from({ length: seriesProp[0].data.length }, (_, i) => String(i + 1)),
          selectedMonth,
          selectedYear,
        )
      : getDaysInMonth(4, 2024);

  const chartSeries = isCustom
    ? seriesProp.map((s) => ({
        id: s.id,
        label: s.label,
        showMark: false,
        curve: 'linear',
        area: true,
        data: Array.isArray(s.data) ? s.data.map((v) => Number(v)) : [],
        ...(s.stack ? { stack: s.stack, stackOrder: s.stackOrder || 'ascending' } : {}),
      }))
    : DEFAULT_SERIES;

  const xAxisLabels =
    xAxisData?.length > 0
      ? xAxisData
      : isCustom && selectedMonth && selectedYear && chartSeries[0]?.data?.length
        ? buildXAxisLabels(
            Array.from({ length: chartSeries[0].data.length }, (_, i) => String(i + 1)),
            selectedMonth,
            selectedYear,
          )
        : getDaysInMonth(4, 2024);

  const xLen = xAxisLabels.length;
  const minDataLen = chartSeries.reduce(
    (acc, s) => Math.min(acc, (s.data || []).length),
    xLen,
  );
  const n = Math.min(xLen, minDataLen);
  const finalXAxis = n > 0 ? xAxisLabels.slice(0, n) : [];
  const finalSeries = chartSeries.map((s) => ({
    ...s,
    data: (s.data || []).slice(0, n),
  }));

  const colorCount = Math.max(chartSeries.length, 3);
  const colorPalette = [
    theme.palette.primary.light,
    theme.palette.primary.main,
    theme.palette.primary.dark,
  ].slice(0, colorCount);
  const gradientIds = chartSeries.map((s) => s.id);

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Typography component="h2" variant="subtitle2" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        {!hideSummary && (
          <Stack sx={{ justifyContent: 'space-between' }}>
            <Stack
              direction="row"
              sx={{
                alignContent: { xs: 'center', sm: 'flex-start' },
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h4" component="p">
                {summaryValue}
              </Typography>
              {trendLabel != null && (
                <Chip size="small" color={trendColor} label={trendLabel} />
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {caption}
            </Typography>
          </Stack>
        )}
        <LineChart
          colors={colorPalette}
          xAxis={[
            {
              scaleType: 'point',
              data: finalXAxis,
              tickInterval: (index, i) => (i + 1) % 5 === 0,
              height: 24,
            },
          ]}
          yAxis={[{ width: 50 }]}
          series={finalSeries}
          height={250}
          margin={{ left: 0, right: 20, top: 20, bottom: 0 }}
          grid={{ horizontal: true }}
          sx={
            gradientIds.length > 0
              ? Object.fromEntries(
                  gradientIds.map((id) => [
                    `& .MuiAreaElement-series-${id}`,
                    { fill: `url('#${id}')` },
                  ]),
                )
              : undefined
          }
          hideLegend={!isCustom}
        >
          {gradientIds.map((id, idx) => (
            <AreaGradient
              key={id}
              color={colorPalette[idx] || theme.palette.primary.main}
              id={id}
            />
          ))}
        </LineChart>
      </CardContent>
    </Card>
  );
}

SessionsChart.propTypes = {
  title: PropTypes.string,
  caption: PropTypes.string,
  summaryValue: PropTypes.string,
  trendLabel: PropTypes.string,
  trendColor: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'info', 'success', 'warning']),
  xAxisData: PropTypes.arrayOf(PropTypes.string),
  series: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      data: PropTypes.arrayOf(PropTypes.number),
      stack: PropTypes.string,
      stackOrder: PropTypes.string,
    }),
  ),
  selectedMonth: PropTypes.number,
  selectedYear: PropTypes.number,
  hideSummary: PropTypes.bool,
};
